import { invariantResponse } from "@epic-web/invariant"
import { useEffect } from "react"
import { type LoaderFunctionArgs, data, type MetaFunction } from "react-router"
import { useLoaderData } from "react-router"
import { Breadcrumbs } from "#app/components/breadcrumbs"
import ProductDetail from "#app/components/admin/products/detail"
import RelatedProducts from "#app/components/related-products"
import { AnalyticsEvents } from "#app/utils/analytics"
import { getCartSessionId } from "#app/utils/cart.server"
import { APP_NAME, BASE_URL } from "#app/utils/constants.js"
import { prisma } from "#app/utils/db.server.ts"
import {
	generateBreadcrumbListJsonLd,
	generateFaqPageJsonLd,
	generateItemListJsonLd,
	generateProductJsonLd,
} from "#app/utils/json-ld.js"
import { productFaqs } from "#app/utils/product-faqs"
import { generateMeta } from "#app/utils/meta.js"
import { useUmami } from "#app/utils/umami"
import routes from "#routes/index.js"
import {
	getPublicProduct,
	getRelatedProducts,
} from "../admin+/products+/product.server"

export async function loader({ request, params }: LoaderFunctionArgs) {
	const { handle } = params
	const { sessionId } = await getCartSessionId(request)

	const product = await getPublicProduct(handle)

	invariantResponse(product, "Not found", {
		status: 404,
	})

	const [cartItem, relatedProducts] = await Promise.all([
		prisma.cartItem.findFirst({
			where: {
				cart: { sessionId },
				productId: product.id,
			},
			select: {
				quantity: true,
			},
		}),
		getRelatedProducts(product.id, product.category),
	])

	const amountInCart = cartItem?.quantity ?? 0

	return data({
		amountInCart,
		product,
		relatedProducts,
	})
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const siteUrl =
		process.env.NODE_ENV === "production"
			? "https://www.gretaspatchwork.com"
			: BASE_URL

	const baseMeta = generateMeta({
		title: `${data?.product.name} - Greta's Patchwork`,
		description: data?.product.isUnique
			? `${data?.product.name} de ${APP_NAME} — Pieza única, hecha a mano e irrepetible. Descubre esta creación artesanal exclusiva de patchwork, elaborada con cuidado y atención al detalle.`
			: `Descubre la belleza atemporal del patchwork con ${data?.product.name} de ${APP_NAME}. Cada pieza de patchwork está hecha a mano con cuidado y atención al detalle, lo que garantiza una calidad y durabilidad excepcionales.`,
		canonical: data
			? routes.public.product.route(data.product.handle)
			: undefined,
		image: data?.product.images?.[0]
			? `${siteUrl}/resources/product-images/${data.product.images[0].id}`
			: undefined,
	})

	if (!data?.product) return baseMeta

	const productJsonLd = generateProductJsonLd(data.product)
	const faqJsonLd = generateFaqPageJsonLd(productFaqs)
	const relatedJsonLd =
		data.relatedProducts.length > 0
			? generateItemListJsonLd(data.relatedProducts)
			: null
	const breadcrumbJsonLd = generateBreadcrumbListJsonLd([
		{ name: "Inicio", url: siteUrl },
		{ name: "Tienda", url: siteUrl },
		{
			name: data.product.name,
			url: `${siteUrl}/product/${data.product.handle}`,
		},
	])

	return [
		...baseMeta,
		{
			"script:ld+json": {
				"@context": "https://schema.org",
				"@graph": [
					productJsonLd,
					faqJsonLd,
					breadcrumbJsonLd,
					...(relatedJsonLd ? [relatedJsonLd] : []),
				],
			},
		},
	]
}

export default function AdminPreviewProduct() {
	const { product, amountInCart, relatedProducts } =
		useLoaderData<typeof loader>()
	const { umami } = useUmami()

	useEffect(() => {
		umami().track(AnalyticsEvents.PRODUCT_VIEW, {
			product_id: product.id,
			product_name: product.name,
			product_price: product.price,
			product_handle: product.handle,
		})
	}, [product.handle])

	return (
		<div className="container my-8">
			<Breadcrumbs
				items={[
					{ label: "Inicio", href: "/" },
					{ label: "Tienda", href: "/" },
					{ label: product.name },
				]}
			/>
			<div className="mt-4">
				<ProductDetail showActions {...product} amountInCart={amountInCart} />
				{/* @ts-ignore */}
				<RelatedProducts products={relatedProducts} />
			</div>
		</div>
	)
}
